import { useState, useEffect } from "react";
import moment from "moment";
import { useGetDriverWorkingTimesForDateLazyQuery } from "@api/graphql/generated/generated-types";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";

export type DriverGroupDefinition = {
  name: string;
  filter: (driver: any) => boolean;
  shouldDim: boolean;
};

interface UseDriverScheduleGroupingProps {
  courierId: string | undefined;
  date: moment.Moment;
  enabled?: boolean;
}

export function useDriverScheduleGrouping({
  courierId,
  date,
  enabled = true,
}: UseDriverScheduleGroupingProps) {
  const { showToast } = useCourieStore();
  const [driverGroupDefinitions, setDriverGroupDefinitions] =
    useState<DriverGroupDefinition[] | undefined>();

  const [
    GetDriverWorkingTimesForDate,
    {
      loading: GetDriverWorkingTimesForDateLoading,
      error: GetDriverWorkingTimesForDateError,
    },
  ] = useGetDriverWorkingTimesForDateLazyQuery();

  useEffect(() => {
    if (!enabled || !courierId) {
      setDriverGroupDefinitions(undefined);
      return;
    }

    const isToday = date.isSame(moment(), "day");
    if (isToday) {
      setDriverGroupDefinitions(undefined);
      return;
    }

    const formattedDate = date.format("YYYY-MM-DD");
    GetDriverWorkingTimesForDate({
      variables: { courierId, date: formattedDate },
      fetchPolicy: "network-only",
    })
      .then((res) => {
        if (!res.data?.driverScheduleForDateRange.driverSchedules) {
          setDriverGroupDefinitions(undefined);
          return;
        }

        const isWorkingByDriverId = Object.fromEntries(
          (res.data?.driverScheduleForDateRange.driverSchedules || []).map(
            (schedule) => {
              const driverId = schedule.driver.id;
              if (schedule.schedule.length !== 1) {
                throw new Error("Driver has multiple schedules");
              }
              if (
                moment(schedule.schedule[0].date).format("YYYY-MM-DD") !==
                formattedDate
              ) {
                throw new Error("Schedule is not the same day");
              }
              const isWorking = schedule.schedule[0].timeBlocks.length > 0;
              return [driverId, isWorking];
            }
          )
        );

        setDriverGroupDefinitions([
          {
            name: "Scheduled",
            shouldDim: false,
            filter: (driver) => isWorkingByDriverId[driver.id],
          },
          {
            name: "Not Scheduled",
            shouldDim: true,
            filter: (driver) => !isWorkingByDriverId[driver.id],
          },
        ]);
      })
      .catch((err) => {
        setDriverGroupDefinitions(undefined);
        showErrorToast(err, showToast);
      });
  }, [courierId, date.format("YYYY-MM-DD"), enabled]);

  return {
    driverGroupDefinitions,
    loading: GetDriverWorkingTimesForDateLoading,
    error: GetDriverWorkingTimesForDateError,
  };
}
